'use memo';

import { ReactNode, useMemo } from 'react';
import { MenuTitles, Pages } from 'constants/menu';
import {
  AgxDivider,
  AgxEmptyState,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxToggleSwitch,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import LeftSubmenu, { SubPage, SubPageStatusIndicator } from 'components/left-submenu/LeftSubmenu';
import { useParams } from 'react-router-dom';
import { useSelectedCampaign } from 'hooks';
import { useStaffGetPropertyInformationProgressForCampaign } from '@urbanx/agx.campaigns.client';
import { FormPrompt } from 'components/FormPrompt';

interface PropertyInformationSubPageProps {
  currentPage: Pages;
  isLoading: boolean;
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES];
  hasUnsavedChanges: boolean;
  onSave: () => void;
  saveButtonId: string;
  saveButtonTestId: string;
  showChildren: boolean;
  hideSaveButton?: boolean;
  children: ReactNode;
}

const PropertyInformationSubPage = ({
  currentPage,
  isLoading,
  saveState,
  hasUnsavedChanges,
  onSave,
  saveButtonId,
  saveButtonTestId,
  hideSaveButton = false,
  children,
}: PropertyInformationSubPageProps) => {
  const { campaignId } = useParams();
  const { campaign } = useSelectedCampaign();
  const blankContractIsLive = campaign?.blankContractIsLive ?? false;
  const {
    data: progressData,
    isPending: progressIsPending,
    isError: progressIsError,
  } = useStaffGetPropertyInformationProgressForCampaign(campaignId ?? '', !!campaignId);

  const getStatus = (key: string): SubPageStatusIndicator | undefined => {
    const value = progressData?.campaignSectionPrepared?.[key];
    if (typeof value !== 'boolean') return undefined;
    return value ? 'success' : 'error';
  };

  const subPages = useMemo<SubPage[]>(() => {
    const showIdentityVerification =
      progressData?.campaignSectionPrepared?.VendorIdentityVerification !== undefined;

    return [
      {
        title: MenuTitles.PROPERTY_DETAILS,
        link: Pages.PROPERTY_INFORMATION_PROPERTY_DETAILS,
        statusIndicator:
          progressIsPending || progressIsError ? undefined : getStatus('PropertyDetails'),
      },
      ...(showIdentityVerification
        ? [
            {
              title: MenuTitles.IDENTITY_VERIFICATION,
              link: Pages.PROPERTY_INFORMATION_IDENTITY_VERIFICATION,
              statusIndicator:
                progressIsPending || progressIsError
                  ? undefined
                  : getStatus('VendorIdentityVerification'),
            },
          ]
        : []),
      {
        title: MenuTitles.VENDOR_DETAILS,
        link: Pages.PROPERTY_INFORMATION_VENDOR_DETAILS,
        statusIndicator:
          progressIsPending || progressIsError ? undefined : getStatus('VendorDetails'),
      },
      {
        title: MenuTitles.CONTRACT,
        link: Pages.PROPERTY_INFORMATION_CONTRACT,
        statusIndicator: progressIsPending || progressIsError ? undefined : getStatus('Contract'),
      },
      {
        title: MenuTitles.TENANCY_INFORMATION,
        link: Pages.PROPERTY_INFORMATION_TENANCY_INFORMATION,
        statusIndicator:
          progressIsPending || progressIsError ? undefined : getStatus('TenancyInformation'),
      },
    ];
  }, [getStatus, progressData?.campaignSectionPrepared, progressIsError, progressIsPending]);

  return (
    <AgxSpacer direction="vertical" gap="spacing-2xl" fill>
      <FormPrompt hasUnsavedChanges={hasUnsavedChanges} />
      <AgxSpacer id="subPageHeadingContainer" direction="vertical" fill>
        <AgxSpacer
          id="subPageHeading"
          direction="horizontal"
          gap="spacing-lg"
          fill
          contentAlignment="center"
          contentJustification="space-between"
        >
          <AgxSpacer direction="horizontal" contentAlignment="start" contentJustification="start">
            <AgxTypography id="pageTitle-Property-Information" type="heading" size="x-small">
              {MenuTitles.PROPERTY_INFORMATION}
            </AgxTypography>
          </AgxSpacer>
          <AgxSpacer
            direction="horizontal"
            gap="spacing-xl"
            contentAlignment="center"
            contentJustification="end"
          >
            <AgxToggleSwitch
              id="toggleContractLive"
              checked={blankContractIsLive}
              offLabel="Offline"
              onLabel="Contract Live"
              onChange={() => undefined}
            />
            {!hideSaveButton && (
              <AgxSaveChangesButton
                key={saveButtonTestId}
                id={saveButtonId}
                dataTestId={saveButtonTestId}
                text="Save Changes"
                size="medium"
                variant="primary"
                state={saveState}
                disabled={!hasUnsavedChanges}
                onClick={onSave}
              />
            )}
          </AgxSpacer>
        </AgxSpacer>
        <AgxDivider variant="horizontal" pushMargin="spacing-3xl" />
      </AgxSpacer>
      <AgxSpacer direction="horizontal" gap="spacing-md" fill>
        <LeftSubmenu currentPage={currentPage} subPages={subPages} />
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          {/*
            Only gate the page content on its own loading state. The submenu
            progress query (`progressIsPending`) is solely used to drive the
            left-hand status indicators, which already render gracefully while
            pending. Coupling the content to it caused a "Loading..." flash on
            first navigation into Property Information.
          */}
          {isLoading ? <AgxEmptyState variant="minimal" label="Loading..." /> : children}
        </AgxSpacer>
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default PropertyInformationSubPage;
